export const EDITOR_MODES = Object.freeze(["compose", "annotate", "weave", "stage"] as const);
export const PLAY_MODES = Object.freeze(["solo-garden", "word-tending"] as const);

export type EditorMode = (typeof EDITOR_MODES)[number];
export type PlayMode = (typeof PLAY_MODES)[number];

export interface CanonicalPoem {
  readonly id: string;
  readonly lines: readonly [string, string, string];
}

export interface SummerRainPack {
  readonly schemaVersion: 1;
  readonly id: "summer-rain@1.0.0";
  readonly source: "local";
  readonly defaults: {
    readonly themeId: "washi-dawn";
    readonly choreographyId: "courtyard";
  };
  readonly modes: {
    readonly editor: readonly EditorMode[];
    readonly play: readonly PlayMode[];
  };
  readonly poems: readonly CanonicalPoem[];
}

export const SUMMER_RAIN_PACK_REF = "summer-rain@1.0.0";
const LOCAL_PACK_ASSET_PATH = "/app/assets/packs/summer-rain@1.0.0.json";
const RELEASED_POEMS = [
  {
    id: "rain-at-eaves",
    lines: ["summer rain gathers", "under the cedar eaves", "one bell answers"],
  },
  {
    id: "moss-courtyard",
    lines: ["moss drinks the rainfall", "quiet stones hold the morning", "sandals wait inside"],
  },
  {
    id: "cloud-break",
    lines: ["clouds loosen their hold", "three swallows cross the pale light", "warm roofs breathe again"],
  },
] as const;

export const SUMMER_RAIN_FEED_POEM: CanonicalPoem = Object.freeze({
  id: RELEASED_POEMS[0].id,
  lines: Object.freeze(RELEASED_POEMS[0].lines),
});

function readExactRecord(value: unknown, keys: readonly string[], label: string): readonly unknown[] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const actualKeys = Reflect.ownKeys(value);
  if (
    actualKeys.length !== keys.length ||
    actualKeys.some((key) => typeof key !== "string" || !keys.includes(key))
  ) {
    throw new TypeError(`${label} contains unknown or missing keys`);
  }
  return keys.map((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}.${key} must be an enumerable data property`);
    }
    const descriptorValue: unknown = descriptor.value;
    return descriptorValue;
  });
}

function readExactArray(value: unknown, expectedLength: number, label: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new TypeError(`${label} must be an array`);
  if (Object.getPrototypeOf(value) !== Array.prototype) {
    throw new TypeError(`${label} must use the Array prototype`);
  }
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    lengthDescriptor === undefined ||
    lengthDescriptor.enumerable ||
    !("value" in lengthDescriptor) ||
    lengthDescriptor.value !== expectedLength
  ) {
    throw new TypeError(`${label} must contain exactly ${expectedLength} items`);
  }
  const indexKeys = Array.from({ length: expectedLength }, (_, index) => String(index));
  const expectedKeys = [...indexKeys, "length"];
  const actualKeys = Reflect.ownKeys(value);
  if (
    actualKeys.length !== expectedKeys.length ||
    actualKeys.some((key) => typeof key !== "string" || !expectedKeys.includes(key))
  ) {
    throw new TypeError(`${label} has invalid array shape`);
  }
  return indexKeys.map((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}[${key}] must be an enumerable data property`);
    }
    const descriptorValue: unknown = descriptor.value;
    return descriptorValue;
  });
}

function assertLiteral<T extends string | number>(value: unknown, expected: T, label: string): asserts value is T {
  if (value !== expected) throw new TypeError(`${label} must be ${JSON.stringify(expected)}`);
}

function parseReleasedModes<T extends string>(value: unknown, released: readonly T[], label: string): readonly T[] {
  const values = readExactArray(value, released.length, label);
  const parsed: string[] = [];
  values.forEach((mode, index) => {
    if (typeof mode !== "string") throw new TypeError(`${label}[${index}] must be a string`);
    parsed.push(mode);
  });
  if (new Set(parsed).size !== parsed.length) throw new TypeError(`${label} contains a duplicate mode`);
  const supported: ReadonlySet<string> = new Set(released);
  if (parsed.some((mode) => !supported.has(mode))) throw new TypeError(`${label} contains an unsupported mode`);
  if (parsed.some((mode, index) => mode !== released[index])) {
    throw new TypeError(`${label} released order mismatch`);
  }
  return Object.freeze(parsed as T[]);
}

function parseReleasedPoem(value: unknown, index: number): CanonicalPoem {
  const label = `pack.poems[${index}]`;
  const [idValue, linesValue] = readExactRecord(value, ["id", "lines"], label);
  if (typeof idValue !== "string") throw new TypeError(`${label}.id must be a string`);
  if (idValue.length === 0) throw new TypeError(`${label}.id must be non-empty`);
  assertLiteral(idValue, RELEASED_POEMS[index].id, `${label}.id`);

  const lineValues = readExactArray(linesValue, 3, `${label}.lines`);
  const lines: string[] = [];
  lineValues.forEach((line, lineIndex) => {
    const lineLabel = `${label}.lines[${lineIndex}]`;
    if (typeof line !== "string") throw new TypeError(`${lineLabel} must be a string`);
    if (line.trim().length === 0) throw new TypeError(`${lineLabel} must be non-empty`);
    assertLiteral(line, RELEASED_POEMS[index].lines[lineIndex], lineLabel);
    lines.push(line);
  });
  return Object.freeze({
    id: idValue,
    lines: Object.freeze(lines as [string, string, string]),
  });
}

export function parseSummerRainPack(value: unknown): SummerRainPack {
  const [schemaVersion, id, source, defaultsValue, modesValue, poemsValue] = readExactRecord(
    value,
    ["schemaVersion", "id", "source", "defaults", "modes", "poems"],
    "pack",
  );
  assertLiteral(schemaVersion, 1, "pack.schemaVersion");
  assertLiteral(id, "summer-rain@1.0.0", "pack.id");
  assertLiteral(source, "local", "pack.source");

  const [themeId, choreographyId] = readExactRecord(
    defaultsValue,
    ["themeId", "choreographyId"],
    "pack.defaults",
  );
  assertLiteral(themeId, "washi-dawn", "pack.defaults.themeId");
  assertLiteral(choreographyId, "courtyard", "pack.defaults.choreographyId");
  const defaults = Object.freeze({ themeId, choreographyId });

  const [editorValue, playValue] = readExactRecord(modesValue, ["editor", "play"], "pack.modes");
  const editor = parseReleasedModes(editorValue, EDITOR_MODES, "pack.modes.editor");
  const play = parseReleasedModes(playValue, PLAY_MODES, "pack.modes.play");
  const modes = Object.freeze({ editor, play });

  const poemValues = readExactArray(poemsValue, RELEASED_POEMS.length, "pack.poems");
  const poems = Object.freeze(poemValues.map(parseReleasedPoem));

  return Object.freeze({ schemaVersion, id, source, defaults, modes, poems });
}

export async function loadSummerRainPack(): Promise<SummerRainPack> {
  let response: Response;
  try {
    response = await fetch(LOCAL_PACK_ASSET_PATH);
  } catch (cause) {
    throw new Error(`Local pack ${LOCAL_PACK_ASSET_PATH} fetch failed`, { cause });
  }
  if (!response.ok) {
    throw new Error(
      `Local pack ${LOCAL_PACK_ASSET_PATH} request failed: ${response.status} ${response.statusText}`,
    );
  }

  let value: unknown;
  try {
    value = await response.json();
  } catch (cause) {
    throw new Error(`Local pack ${LOCAL_PACK_ASSET_PATH} JSON decode failed`, { cause });
  }

  try {
    return parseSummerRainPack(value);
  } catch (cause) {
    throw new TypeError(`Local pack ${LOCAL_PACK_ASSET_PATH} schema invalid`, { cause });
  }
}
